import { useTranslation } from 'react-i18next';
import Card from './Card.jsx';
import Badge from './Badge.jsx';
import ScoreGauge from './ScoreGauge.jsx';
import EmptyState from './EmptyState.jsx';

/**
 * The written hand-over a sales rep reads before a meeting.
 *
 * Shown in two places - the lead page and the customer page - so it lives here
 * rather than being copied. The customer page is the one that matters: it is
 * where you land from the agenda, so the answers, the summary and what we should
 * propose have to be on that screen, not one click further.
 *
 * `answers` renders the full question-by-question list; hide it with
 * `showAnswers={false}` where the page already lists them.
 */
const LeadBrief = ({ brief, showAnswers = true, compact = false }) => {
  const { t } = useTranslation();

  if (!brief) return <EmptyState title={t('leads.briefPending')} />;

  return (
    <>
      <div className={compact ? 'brief brief--compact' : 'brief'}>
        <div className="brief__head">
          <div>
            <p className="brief__headline">{brief.headline}</p>
            <div className="brief__tags">
              <Badge value={brief.temperature} label={t(`leads.temperatures.${brief.temperature}`)} />
              {brief.complexity ? (
                <Badge value={brief.complexity} label={t(`leads.complexities.${brief.complexity}`)} />
              ) : null}
              {brief.autonomy ? (
                <Badge value="WARM" label={t(`leads.autonomies.${brief.autonomy}`, brief.autonomy)} />
              ) : null}
            </div>
          </div>
          <ScoreGauge score={brief.score} temperature={brief.temperature} compact />
        </div>

        <section className="brief__block">
          <h3>{t('leads.briefSituation')}</h3>
          <p>{brief.situation}</p>
        </section>

        <section className="brief__block">
          <h3>{t('leads.briefProblem')}</h3>
          <p>{brief.problem}</p>
        </section>

        {/* The prospect's own sentences - quoted, never paraphrased. */}
        {brief.inTheirWords?.length ? (
          <section className="brief__block">
            <h3>{t('leads.briefWords')}</h3>
            {brief.inTheirWords.map((item) => (
              <blockquote key={item.key} className="brief__quote">
                <span className="brief__quote-label">{item.label}</span>
                <p>{item.text}</p>
              </blockquote>
            ))}
          </section>
        ) : null}

        {/* What to sell him: the agent or automation, and what it must connect to. */}
        <section className="brief__block brief__block--accent">
          <h3>{t('leads.briefRecommendation')}</h3>
          <p>{brief.recommendation}</p>
          {brief.integrations?.length ? (
            <p className="brief__integrations">
              <strong>{t('leads.briefIntegrations')}:</strong> {brief.integrations.join(' · ')}
            </p>
          ) : null}
        </section>

        <section className="brief__block">
          <h3>{t('leads.briefMeeting')}</h3>
          <ul className="brief__list">
            {brief.meetingPlan?.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
      </div>

      {showAnswers && brief.answers?.length ? (
        <Card title={`${t('leads.briefAnswers')} (${brief.answers.length})`}>
          <dl className="detail-list">
            {brief.answers.map((answer) => (
              <div key={answer.key}>
                <dt>{answer.label}</dt>
                <dd>{answer.value}</dd>
              </div>
            ))}
          </dl>
        </Card>
      ) : null}
    </>
  );
};

export default LeadBrief;
